// TODO - cleanup & move to shared package
import { useRemoteFeatureFlag } from '@utils/remoteFeatureFlags/useRemoteFeatureFlag';
import { getLocalStorage } from '@utils/storage';
import { useQueryParamHelper } from '@utils/queryString';
import { useEffect, useMemo, useState } from 'react';
import { addError } from '@utils/logger';
import { useTimeout } from '@hooks/useTimeout';

enum CurrencyCode {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  USD = 'USD',
}

enum CultureCode {
  AU = 'AU',
  CA = 'CA',
  DE = 'DE',
  ES = 'ES',
  FR = 'FR',
  IT = 'IT',
  GB = 'GB',
  JA = 'JA',
  US = 'US',
}

enum LanguageCode {
  de = 'de',
  en = 'en',
  es = 'es',
  fr = 'fr',
  it = 'it',
  ja = 'ja',
}

type GeoBasedLocalePayload = {
  currency?: CurrencyCode;
  culture?: CultureCode;
  language?: LanguageCode;
};

const DefaultLocalePayload: GeoBasedLocalePayload = {
  currency: CurrencyCode.USD,
  culture: CultureCode.US,
  language: LanguageCode.en,
};

const defaultLocales: GeoBasedLocalePayload[] = [
  DefaultLocalePayload,
  {
    culture: CultureCode.AU,
    currency: CurrencyCode.AUD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.CA,
    currency: CurrencyCode.CAD,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.DE,
    currency: CurrencyCode.EUR,
    language: LanguageCode.de,
  },
  {
    culture: CultureCode.ES,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.FR,
    currency: CurrencyCode.EUR,
    language: LanguageCode.fr,
  },
  {
    culture: CultureCode.IT,
    currency: CurrencyCode.EUR,
    language: LanguageCode.it,
  },
  {
    culture: CultureCode.US,
    currency: CurrencyCode.EUR,
    language: LanguageCode.es,
  },
  {
    culture: CultureCode.GB,
    currency: CurrencyCode.GBP,
    language: LanguageCode.en,
  },
  {
    culture: CultureCode.JA,
    currency: CurrencyCode.JPY,
    language: LanguageCode.ja,
  },
];

const findClosestMatchingLocale = (payload?: GeoBasedLocalePayload): GeoBasedLocalePayload | undefined => {
  if (!payload || (!payload.currency && !payload.culture && !payload.language)) {
    return undefined;
  }

  if (payload.currency && payload.language && payload.culture) {
    return payload;
  }

  // handle extended language codes (eg. en-AU, es-US)
  if (!payload.culture && payload.language && payload.language.length > 2) {
    const culture = validatedCultureCode(payload.language.slice(3));
    if (culture) {
      payload.culture = culture;
    }
  }

  const matchingLocale = defaultLocales.find((locale) => {
    let matchCount = 0;
    let totalProperties = 0;

    if (payload.currency) {
      totalProperties++;
      if (payload.currency.toUpperCase() === locale.currency) {
        matchCount++;
      }
    }
    if (payload.culture) {
      totalProperties++;
      if (payload.culture.toUpperCase() === locale.culture) {
        matchCount++;
      }
    }
    if (payload.language) {
      totalProperties++;
      if (payload.language.slice(0, 2).toLowerCase() === locale.language) {
        matchCount++;
      }
    }

    return matchCount === totalProperties;
  });

  if (matchingLocale) {
    return matchingLocale;
  }
};

const constructLanguageCodeIso15897 = (language?: LanguageCode, culture?: CultureCode): string | undefined => {
  if (!language || !culture) {
    return undefined;
  }
  return `${language}-${culture}`;
};

const validatedCurrencyCode = (code: unknown): CurrencyCode | undefined => {
  if (typeof code === 'string' && Object.values(CurrencyCode).includes(code.toUpperCase() as CurrencyCode)) {
    return code.toUpperCase() as CurrencyCode;
  }
};

const validatedCultureCode = (code: unknown): CultureCode | undefined => {
  if (typeof code === 'string' && Object.values(CultureCode).includes(code.toUpperCase() as CultureCode)) {
    return code.toUpperCase() as CultureCode;
  }
};
const validatedLanguageCode = (code: unknown): LanguageCode | undefined => {
  if (typeof code !== 'string' || code.length < 2) {
    return;
  }

  // ony validate the first 2 characters
  const languageCode = code.slice(0, 2).toLowerCase();

  return Object.values(LanguageCode).includes(languageCode as LanguageCode) ? (languageCode as LanguageCode) : undefined;
};

const validatedLocale = (object: unknown) => {
  const currencyCode = validatedCurrencyCode((object as GeoBasedLocalePayload)?.currency);
  const cultureCode = validatedCultureCode((object as GeoBasedLocalePayload)?.culture);
  const languageCode = validatedLanguageCode((object as GeoBasedLocalePayload)?.language);

  if (currencyCode || cultureCode || languageCode) {
    return {
      currency: currencyCode,
      culture: cultureCode,
      language: languageCode,
    } as GeoBasedLocalePayload;
  }
};

const getLocallyStoredGeoBasedLocale = (): {
  value: GeoBasedLocalePayload | undefined;
  setValue: (locale: GeoBasedLocalePayload) => void;
} => {
  const localStorage = getLocalStorage();
  const storedLocale = localStorage.getItem('joyLocale');

  const setValue = (updatedLocale: GeoBasedLocalePayload) => {
    const locale = validatedLocale(updatedLocale);
    if (locale) {
      localStorage.setItem('joyLocale', JSON.stringify(locale));
    }
  };

  if (!storedLocale) {
    return { value: undefined, setValue };
  }

  try {
    const locale = validatedLocale(JSON.parse(storedLocale));
    return { value: locale, setValue };
  } catch (error) {
    addError(error as Error, {});
  }
  return { value: undefined, setValue };
};

const useSearchParamsGeoLocale = (): Partial<GeoBasedLocalePayload> | undefined => {
  const { getValueString } = useQueryParamHelper();

  const currency = getValueString('currency') || getValueString('cur');
  const culture = getValueString('culture') || getValueString('cul');
  const language = getValueString('language') || getValueString('lng') || getValueString('lang') || getValueString('l');

  if (currency || culture || language) {
    return {
      currency: validatedCurrencyCode(currency),
      culture: validatedCultureCode(culture),
      language: validatedLanguageCode(language),
    };
  }
};

const useRemoteGeoBasedLocale = (): { loading: boolean; value: GeoBasedLocalePayload | undefined } => {
  const { payload, loading } = useRemoteFeatureFlag('geoBasedLocale');
  const locale = validatedLocale(payload);
  return { loading, value: locale };
};

const syncToLocal = (values: GeoBasedLocalePayload, setter: ReturnType<typeof getLocallyStoredGeoBasedLocale>['setValue']): void => {
  if (values) {
    setter(values);
  }
};

const addLanguageCodeIso15897 = (locale?: GeoBasedLocalePayload): undefined | GeoBasedLocaleWithIso15897 => {
  return locale
    ? {
        ...locale,
        languageCodeIso15897: constructLanguageCodeIso15897(locale.language, locale.culture),
      }
    : undefined;
};

type GeoBasedLocaleWithIso15897 = GeoBasedLocalePayload & {
  languageCodeIso15897?: string;
};

type GeoBasedLocaleHookReturn = Readonly<{
  loading: boolean;
  value: GeoBasedLocaleWithIso15897 | undefined;
}>;

export const useGeoBasedLocale = (defaultValues?: GeoBasedLocalePayload): GeoBasedLocaleHookReturn => {
  const { value: remoteValues, loading: remoteLocaleLoading } = useRemoteGeoBasedLocale();
  const { value: localValues, setValue: setLocalValues } = getLocallyStoredGeoBasedLocale();
  const searchParams = useSearchParamsGeoLocale();
  const [languageCode, setLanguageCode] = useState<GeoBasedLocaleWithIso15897 | undefined>();

  const [loading, setLoading] = useState(true);

  // timeout if remote locale can't be loaded in 3 seconds
  useTimeout(() => {
    setLoading(false);
  }, 3000);

  useEffect(() => {
    if (!loading) {
      return;
    }

    if (languageCode || !remoteLocaleLoading) {
      setLoading(false);
    }
  }, [loading, remoteLocaleLoading, languageCode]);

  useEffect(() => {
    if (languageCode || (!searchParams && !localValues && !remoteValues)) {
      return;
    }
    try {
      const searchParamLocale = findClosestMatchingLocale(searchParams);
      const localStorageLocale = findClosestMatchingLocale(localValues);
      const remoteLocale = findClosestMatchingLocale(remoteValues);
      const fallbackLocale = !loading ? defaultValues ?? DefaultLocalePayload : undefined;

      // use the first defined locale source, prioritized by queryString > localStorage > Amplitude > fallback locale
      let locale = searchParamLocale ?? localStorageLocale ?? remoteLocale ?? fallbackLocale;

      if (locale) {
        syncToLocal(locale, setLocalValues);
        setLanguageCode(addLanguageCodeIso15897(locale));
      }
    } catch (error) {
      addError(error as Error, {});
      console.error(error);
    }
  }, [defaultValues, languageCode, loading, localValues, remoteValues, searchParams, setLocalValues]);

  return useMemo(
    () => ({
      loading: loading,
      value: loading ? undefined : languageCode,
    }),
    [languageCode, loading]
  );
};
